// Debugger snapshots recorded alongside the trace, and the lazy object-graph
// view that renders them.
//
// The interpreter executes via nested JS generators, so a thread's call stack
// and locals live only in the (transient) generator chain and `Scope` objects.
// To power a VSCode-style debugger we reify a call-frame stack on each `Thread`
// and, at every trace event, record a snapshot: each live thread's frames, the
// locals of every frame, and the current transaction's read/write sets.
//
// The heart of the debugger is exploring the whole object graph *as it was* at a
// chosen (time, thread) point. We don't serialize that graph — it can be deep and
// cyclic. Instead we lean on the MVCC store, which is already a persistent
// structure versioned over time: the store retained on `RunResult` holds every
// version of every object, so a snapshot needs only to record the *roots* (locals
// as raw values, the transaction's addresses + buffered writes) plus the store
// *version* to read against. A `GraphView` then resolves any address lazily, and
// `valueNode` turns a value into an on-demand expandable tree — struct into
// fields, pointer into the object it points at, collection into its entries — so
// the UI can walk the graph to any depth without the snapshot ever holding it.

import { Store } from "./store.js";
import { Address, StoredObject, Value, displayValue } from "./value.js";

// ---------------------------------------------------------------------------
// Snapshot: lightweight roots, index-aligned with the trace.
// ---------------------------------------------------------------------------

/** One local variable binding, captured as its raw (immutable) runtime value. */
export interface DebugBinding {
  name: string;
  value: Value;
}

/** One call frame: a function/method activation and the locals in scope in it. */
export interface DebugFrame {
  /** Display label, e.g. `transfer` or `Account.deposit`. */
  fn: string;
  /** 1-based line the frame is currently executing. */
  line: number;
  /** Source file the line indexes into: `undefined` = user program, else a stdlib id. */
  source?: string;
  /** In-scope locals, outermost (params) first. */
  locals: DebugBinding[];
}

/** The current transaction of a thread (at most one per thread). Its read/write
 *  sets are addresses into the store; values are resolved lazily via a `GraphView`. */
export interface DebugTxn {
  id: number;
  /** The snapshot version the transaction reads against. */
  readVersion: number;
  /** Addresses the transaction has read, sorted. */
  reads: Address[];
  /** The transaction's buffered writes (null = a delete/tombstone), sorted by address. */
  writes: { addr: Address; obj: StoredObject | null }[];
}

/** What a blocked thread is waiting for, if anything: a 2PL thread waiting to
 *  acquire its whole footprint before its transaction can begin. Absent/null means
 *  the thread is not blocked on locks. (A `retry` park is *not* here — it has
 *  abandoned an attempt rather than blocked before one, so it lives in
 *  `prevAttempt`.) */
export type DebugWaiting = { kind: "lock"; txn: number; addrs: Address[] };

/** The 2PL locks a thread holds for a transaction it has *not yet begun*. Under
 *  the hybrid strategy a lock txn acquires its whole footprint before `begin`, so
 *  between `lock-acquire` and `txn-begin` the thread holds locks for a known txn id
 *  with no live `Transaction` yet — this is what keeps that txn visible in the
 *  graph across that window (and any scheduler interleavings within it). Once the
 *  txn begins, `txn` carries it instead and this is null. */
export interface DebugHolding {
  txn: number;
  addrs: Address[];
}

/**
 * The attempt this thread just abandoned, retained until its replacement begins.
 *
 * `retry` and abort are the same mechanism pointed in opposite temporal
 * directions — both discard the buffered writes and re-run the body under a fresh
 * transaction id, keyed on the *read set*. They differ only in whether that read
 * set has already changed:
 *
 *   - `"abort"` — it has, so there is a reason to re-run *now* (the re-run follows
 *     immediately, with no scheduling point in between). `reads` + `readVersion`
 *     still identify the read that went stale — why it aborted.
 *   - `"retry"` — it has not; the transaction was valid and simply declined the
 *     state it found, so the thread parks on `reads` until a committed write
 *     overlaps them. This one spans many events: the whole parked stretch.
 *
 * (`execOccTxn` makes the equivalence concrete: a `retry` whose read set is
 * already stale skips parking and re-runs at once — becoming an abort.)
 */
export interface DebugPrevAttempt {
  txn: number;
  reads: Address[];
  /** Buffered writes at the moment it was abandoned — never committed, and rolled
   *  back either way. Shown for an abort (what it was attempting); a parked txn is
   *  not attempting anything while it waits, so its writes aren't drawn. */
  writes: Address[];
  /** Its snapshot version, so a stale read can still be identified. */
  readVersion: number;
  ended: "retry" | "abort";
}

/**
 * What the scheduler is doing with a thread at one instant.
 *
 *   - `running`  — the thread whose step produced this event; the one executing.
 *   - `ready`    — runnable, sitting in the ready queue waiting to be picked.
 *   - `sleeping` — in the queue, but at a future virtual time (`sleep`).
 *   - `parked-lock`  — blocked acquiring a 2PL footprint; `waiting` says on what.
 *   - `parked-retry` — blocked in `retry`, waiting for a committed write to
 *     overlap the read set its abandoned attempt (`prevAttempt`) parked on.
 *
 * Derived from the scheduler's own queues at capture time rather than inferred in
 * the UI, because the two park states are not both readable from a thread's own
 * fields: a `retry` park abandons its transaction (see `DebugPrevAttempt`), and
 * `prevAttempt` outlives the park itself — it stays set from the wake until the
 * replacement attempt begins, a stretch in which the thread is plainly `ready`.
 */
export type DebugThreadState =
  | "running"
  | "ready"
  | "sleeping"
  | "parked-lock"
  | "parked-retry";

/** True for the states in which a thread cannot make progress on its own. */
export function isParked(state: DebugThreadState): boolean {
  return state === "parked-lock" || state === "parked-retry";
}

/** One thread's state at a point in time. */
export interface DebugThread {
  id: number;
  /** What the scheduler is doing with this thread right now. */
  state: DebugThreadState;
  /** The thread's entry function name (its lane label in the trace). */
  entryFn: string;
  /** Call frames, outermost (thread entry) first, innermost (current) last. */
  frames: DebugFrame[];
  /** The current transaction, or null when the thread is not in a `txn`. */
  txn: DebugTxn | null;
  /** Non-null when the thread is blocked acquiring a 2PL footprint. */
  waiting?: DebugWaiting | null;
  /** Non-null when the thread holds 2PL locks for a txn that hasn't begun yet. */
  holding?: DebugHolding | null;
  /** The attempt just abandoned — parked on a `retry`, or aborted — kept until its
   *  replacement begins (see `DebugPrevAttempt`). */
  prevAttempt?: DebugPrevAttempt | null;
}

/** A snapshot of every live thread at one trace event. */
export interface DebugSnapshot {
  /** The id of the thread that produced the event this snapshot accompanies. */
  actingThread: number;
  /** The store's committed version at this event — what an auto (non-transactional)
   *  read sees, and the base version for resolving the object graph. */
  committedVersion: number;
  /** Every thread that is alive at this moment, sorted by id. */
  threads: DebugThread[];
}

// ---------------------------------------------------------------------------
// Lazy object-graph view over the retained store.
// ---------------------------------------------------------------------------

/** A read-only view of memory at one point in time: the MVCC store read at a
 *  fixed version, optionally overlaid with a transaction's buffered writes (so a
 *  thread mid-transaction sees its own not-yet-committed changes). */
export class GraphView {
  constructor(
    private readonly store: Store,
    private readonly version: number,
    private readonly overlay?: Map<Address, StoredObject | null>
  ) {}

  read(addr: Address): StoredObject | undefined {
    if (this.overlay?.has(addr)) return this.overlay.get(addr) ?? undefined;
    return this.store.read(addr, this.version)?.value ?? undefined;
  }

  /** Direct child addresses of a collection marker, overlay applied. */
  liveChildren(addr: Address): Address[] {
    const needle = addr + "/";
    const out = new Set(this.store.liveChildren(addr, this.version));
    if (this.overlay) {
      for (const [a, v] of this.overlay) {
        if (!a.startsWith(needle) || a.indexOf("/", needle.length) !== -1) continue;
        if (v === null) out.delete(a);
        else out.add(a);
      }
    }
    return [...out].sort();
  }
}

/** The memory view a thread sees at a snapshot: its transaction's snapshot +
 *  buffered writes when it is in one, else the committed store. */
export function threadView(store: Store, snap: DebugSnapshot, thread: DebugThread): GraphView {
  if (thread.txn) {
    const overlay = new Map(thread.txn.writes.map((w) => [w.addr, w.obj] as const));
    return new GraphView(store, thread.txn.readVersion, overlay);
  }
  return new GraphView(store, snap.committedVersion);
}

// ---------------------------------------------------------------------------
// Turning a value / address into an on-demand expandable tree node.
// ---------------------------------------------------------------------------

/** One node of the object-graph tree: a one-line `summary` shown collapsed, and —
 *  when the value has structure — a `children` thunk called lazily on expand.
 *  Laziness is what keeps deep or cyclic graphs finite: nothing is walked until
 *  the user opens it. */
export interface GraphNode {
  summary: string;
  /** Present iff expandable; computed on demand. */
  children?: () => GraphChild[];
}

export interface GraphChild {
  /** Field name, collection key, or `∈` for set membership. */
  label: string;
  node: GraphNode;
}

/** A shallow, one-line rendering of a value — type + `{…}` for aggregates, the
 *  literal for scalars — so a collapsed row says what a thing is without
 *  expanding the whole graph under it. */
function summarize(v: Value, view: GraphView): string {
  if (v.kind === "ptr") {
    const obj = view.read(v.addr);
    if (!obj) return `${v.addr} <null>`;
    if (obj.kind === "struct") return hasFields(obj.fields) ? `${obj.typeName} {…}` : `${obj.typeName} {}`;
    if (obj.kind === "collection") return `${obj.container}<${obj.typeArgs.join(", ")}>`;
    if (obj.kind === "entry") return "entry";
    return "keyset";
  }
  if (v.kind === "record") return hasFields(v.fields) ? `${v.typeName} {…}` : `${v.typeName} {}`;
  if (v.kind === "enum") {
    const keys = Object.keys(v.fields);
    return keys.length ? `${v.variant}(…)` : v.variant;
  }
  // Scalars, functions, closures, iterators: their own rendering (no store needed).
  return displayValue(v);
}

function hasFields(fields: Record<string, Value>): boolean {
  return Object.keys(fields).length > 0;
}

/** The tree node for an address (a transaction read-set cell): resolve it and
 *  render the object it holds, expandable like any pointer target. */
export function nodeForAddr(addr: Address, view: GraphView): GraphNode {
  return valueNode({ kind: "ptr", addr }, view);
}

/** The tree node for a runtime value, resolving pointers through `view`. */
export function valueNode(v: Value, view: GraphView): GraphNode {
  const summary = summarize(v, view);
  switch (v.kind) {
    case "ptr":
      return addrNode(v.addr, summary, view);
    case "record":
    case "enum": {
      const entries = Object.entries(v.fields);
      if (entries.length === 0) return { summary };
      return { summary, children: () => entries.map(([label, fv]) => ({ label, node: valueNode(fv, view) })) };
    }
    default:
      return { summary };
  }
}

/** The tree node for a stored object at `addr` (a pointer target, or a read/write
 *  set cell) — struct fields, collection entries, or a leaf. */
export function addrNode(addr: Address, summary: string, view: GraphView): GraphNode {
  const obj = view.read(addr);
  if (!obj) return { summary: summary || `${addr} <null>` };
  switch (obj.kind) {
    case "struct": {
      const entries = Object.entries(obj.fields);
      if (entries.length === 0) return { summary };
      return { summary, children: () => entries.map(([label, fv]) => ({ label, node: valueNode(fv, view) })) };
    }
    case "collection": {
      const children = view.liveChildren(addr);
      if (children.length === 0) return { summary: `${summary} (empty)` };
      return {
        summary: `${summary} (${children.length})`,
        children: () => children.map((ca) => entryChild(ca, view)),
      };
    }
    case "entry":
      return entryChild(addr, view).node;
    case "keyset":
      return { summary: "keyset" };
  }
}

/** A collection entry as a child: `key → value` for a dict/list, `∈ key` for a set. */
function entryChild(addr: Address, view: GraphView): GraphChild {
  const obj = view.read(addr);
  if (!obj || obj.kind !== "entry") return { label: addr, node: { summary: "absent" } };
  if (obj.value.kind === "void") {
    // Set membership: the element itself is the interesting thing to expand.
    return { label: "∈", node: valueNode(obj.key, view) };
  }
  return { label: displayValue(obj.key), node: valueNode(obj.value, view) };
}

/** The tree node for a transaction write-set cell (a buffered object, possibly a
 *  tombstone) — rendered without needing the store, since the object is in hand. */
export function writeNode(obj: StoredObject | null, addr: Address, view: GraphView): GraphNode {
  if (obj === null) return { summary: "<deleted>" };
  if (obj.kind === "struct") {
    const entries = Object.entries(obj.fields);
    const summary = hasFields(obj.fields) ? `${obj.typeName} {…}` : `${obj.typeName} {}`;
    if (entries.length === 0) return { summary };
    return { summary, children: () => entries.map(([label, fv]) => ({ label, node: valueNode(fv, view) })) };
  }
  if (obj.kind === "entry") return entryChild(addr, view).node;
  if (obj.kind === "collection") return { summary: `${obj.container}<${obj.typeArgs.join(", ")}>` };
  return { summary: "keyset↑" };
}
